import {
	NavigationBuilderStrategy,
	NavigationIcons,
	NavigationModuleSchema,
	NavigationSchema,
	NavigationTabTitleCodes,
	NavigationTitleCodes,
	NavigationUrls,
	PageTypeIds,
} from './types';
import { ToolNavigationData } from '../toolNavigation/types';
import { isArray } from 'lodash';
import { isNullOrUndefined } from '../../predicates/type';

export class NavigationBuilder {
	private readonly schema: NavigationSchema;
	private readonly icons: NavigationIcons;
	private readonly titleCodes: NavigationTitleCodes;
	private readonly urls: NavigationUrls;
	private pageTypeIds: Array<PageTypeIds>;
	public tabTitleCodes: NavigationTabTitleCodes;

	constructor(strategy: NavigationBuilderStrategy) {
		if (!strategy) {
			console.error('Strategy is required');
			return;
		}

		this.pageTypeIds = [];
		this.schema = strategy.getSchema();
		this.icons = strategy.getIcons();
		this.titleCodes = strategy.getTitleCodes();
		this.urls = strategy.getUrls();
		this.tabTitleCodes = strategy.getTabTitleCodes();
	}

	private hasPageAccess = (pageTypeId: PageTypeIds) => this.pageTypeIds.includes(pageTypeId);

	private hasNestedItems = (childrenPageTypeIds: Array<PageTypeIds>) => {
		return isArray(childrenPageTypeIds) && childrenPageTypeIds.length > 0;
	};

	private buildNestedItems = (parentPageTypeId: PageTypeIds, childrenPageTypeIds: Array<PageTypeIds>) => {
		return childrenPageTypeIds.reduce((navigationItems, childPageTypeId) => {
				navigationItems.push({
					pageTypeId: childPageTypeId,
					titleCode: this.titleCodes[childPageTypeId],
					url: this.urls[childPageTypeId],
				});

				return navigationItems;
			}, [{
				// Add the parent item as the first child
				pageTypeId: parentPageTypeId,
				titleCode: this.titleCodes[parentPageTypeId],
				url: this.urls[parentPageTypeId],
			}],
		);
	};

	private buildPrimaryItem = ({
			titleCode,
			parentPageTypeId,
			childrenPageTypeIds,
		}: {
			titleCode: number
			parentPageTypeId: PageTypeIds,
			childrenPageTypeIds?: Array<PageTypeIds>,
		},
	) => {
		return {
			pageTypeId: parentPageTypeId,
			titleCode,
			url: this.urls[parentPageTypeId],
			icon: this.icons[parentPageTypeId],
			childNavigationItems: this.hasNestedItems(childrenPageTypeIds)
				? this.buildNestedItems(parentPageTypeId, childrenPageTypeIds)
				: [],
		};
	};

	private buildModule(schema: NavigationModuleSchema) {
		return schema.reduce((moduleItems, schemaRecord) => {
			const hasParentPageAccess = this.hasPageAccess(schemaRecord.pageTypeId);
			const accessibleChildrenPageTypeIds = this.hasNestedItems(schemaRecord.childrenPageTypeIds)
				? schemaRecord.childrenPageTypeIds.filter(i => this.hasPageAccess(i))
				: [];

			if (hasParentPageAccess) {
				moduleItems.push(
					this.buildPrimaryItem({
						parentPageTypeId: schemaRecord.pageTypeId,
						titleCode: schemaRecord.titleCode,
						childrenPageTypeIds: accessibleChildrenPageTypeIds,
					}),
				);
			} else if (accessibleChildrenPageTypeIds.length === 1) {
				moduleItems.push(
					this.buildPrimaryItem({
						parentPageTypeId: accessibleChildrenPageTypeIds[0],
						titleCode: schemaRecord.titleCode,
					}),
				);
			} else if (accessibleChildrenPageTypeIds.length > 1) {
				const [parentPageTypeId, ...childrenPageTypeIds] = accessibleChildrenPageTypeIds;

				moduleItems.push(
					this.buildPrimaryItem({
						parentPageTypeId,
						titleCode: schemaRecord.titleCode,
						childrenPageTypeIds,
					}),
				);
			}

			return moduleItems;
		}, []);
	}

	public build(pageTypeIds: Array<PageTypeIds>) {
		if (isNullOrUndefined(pageTypeIds)) {
			console.error('Provide pageTypeIds');
			return;
		}

		this.pageTypeIds = pageTypeIds;

		if (isNullOrUndefined(this.schema)) {
			console.error('Provide strategy before build');
			return;
		}

		const navigation: Partial<ToolNavigationData> = {
			tabTitleCodes: this.tabTitleCodes,
		};

		for (const key in this.schema) {
			navigation[key] = this.buildModule(this.schema[key]);
		}

		return navigation as ToolNavigationData;
	}
}
